<template>
  <mf-loading-dialog :loading="loadingDialog">
    <base-page-layout
      :title="$t('campanhas.resultados_campanha')"
      :hide-breadcrumbs="$getFirstValue()"
      hide-ecommerce-configuration
      use-new-filters
      :filters="['lojas']"
      :filter-to-units="campaignUnitIds"
    >
      <v-row>
        <v-col>
          <info-card
            :title="campaign.title"
            :description="campaign.description"
            :date="campaign.message.send_date"
            :time="campaign.message.send_time"
            :segments="campaign.segments"
            :products="campaign.products"
            :initial-date="campaign.initial_date"
            :final-date="campaign.final_date"
            :status="campaign.status"
            :frequency="campaign.frequency"
            :targeting-mode="campaign.targeting_mode"
            channel="push"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="pl-4">
          <span class="section-title">Resultados gerais</span>
        </v-col>
      </v-row>

      <v-row v-if="!hasLoginCard" class="align-stretch">
        <v-col cols="8" class="d-flex">
          <push-notification-preview
            :notification-title="campaign.message.title"
            :notification-text="campaign.message.text"
            :inner-message-title="campaign.message.inner_message_title"
            :inner-message-text="campaign.message.inner_message_text"
          />
        </v-col>
        <v-col cols="4" class="d-flex flex-column">
          <v-row v-for="(card, index) in allCards" :key="index">
            <v-col :class="[!index ? 'pt-0' : '', index === allCards.length - 1 ? 'pb-0' : '']">
              <cbm-card
                :number-text="card.value"
                :title="card.text"
                :is-currency="card.isCurrency"
                :boolean-loader="loadingMetrics"
                :tooltip="card.helpText"
                :percent-text="card.underText"
                :percent-description="card.description"
                :percentage-tooltip="card.percentageTooltip"
                simple-card
                unset-percentage-colors
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row v-if="hasLoginCard">
        <v-col v-for="(card, index) in firstRowCards" :key="index">
          <cbm-card
            :number-text="card.value"
            :title="card.text"
            :is-currency="card.isCurrency"
            :boolean-loader="loadingMetrics"
            :tooltip="card.helpText"
            :percent-text="card.underText"
            :percent-description="card.description"
            :percentage-tooltip="card.percentageTooltip"
            simple-card
            unset-percentage-colors
          />
        </v-col>
      </v-row>

      <v-row v-if="hasLoginCard">
        <v-col v-for="(card, index) in secondRowCards" :key="index">
          <cbm-card
            :number-text="card.value"
            :title="card.text"
            :is-currency="card.isCurrency"
            :boolean-loader="loadingMetrics"
            :tooltip="card.helpText"
            :percent-text="card.underText"
            :percent-description="card.description"
            :percentage-tooltip="card.percentageTooltip"
            simple-card
            unset-percentage-colors
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <funnel :loading="loadingMetrics" :metrics="metrics" :campaign="campaign" :units-configuration="unitsConfiguration" />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <products-table :loading="loadingMetrics" :metrics="metrics" :campaign="campaign" :units-configuration="unitsConfiguration" />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <sendings :loading="loadingMetrics" :sendings="sendings" :show-cost="false" :campaign-id="getSafe(() => campaign._id)" campaign-type="push" />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <mf-action-buttons :primary-button="primaryButton" :cancel-button="cancelButton" />
        </v-col>
      </v-row>
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import { QUERY_GET_CAMPAIGN_METRICS } from '@/modules/campanhas/views/components/graphql'
import { localeString } from '@/config/helpers.js'
import { mapState } from 'vuex'
import { actionButtons } from '../../../views/mixins/action-buttons.js'
import cloneDeep from 'lodash/cloneDeep'
import { QUERY_GET_CAMPAIGN_BY_ID } from '../../graphql'
import offerScheduleCampaign from '@/modules/campanhas/views/mixins/offer-schedule-campaign.js'
import CbmCard from '@/components/CbmCard.vue'

export default {
  components: {
    BasePageLayout: () => import('@/components/BasePageLayout.vue'),
    InfoCard: () => import('@/modules/campanhas/views/components/ResultadosInfoCard.vue'),
    Funnel: () => import('@/modules/campanhas/views/components/results/Funnel.vue'),
    ProductsTable: () => import('@/modules/campanhas/views/components/results/ProductsTable.vue'),
    Sendings: () => import('@/modules/campanhas/views/components/results/Sendings.vue'),
    PushNotificationPreview: () => import('@/modules/campanhas/campanhas-aplicativo/views/components/PushNotificationPreview.vue'),
    CbmCard,
  },

  mixins: [actionButtons, offerScheduleCampaign],

  data: () => ({
    loadingMetrics: false,
    loadingDialog: false,
    metrics: {},
    locale: localeString,
    sendings: [],
    campaignUnitIds: [],
    redirectPage: 'aplicativo',
    campaignId: '',
    campaign: {
      title: '',
      description: '',
      initial_date: '',
      final_date: '',
      products: [],
      segments: [],
      status: 'DRAFT',
      template_id: '',
      frequency: {
        type: 'ONETIME',
        interval: 0,
      },
      message: {
        send_date: '',
        send_time: '',
        title: '',
        text: '',
        inner_message_title: '',
        inner_message_text: '',
        innerMessage: false,
      },
      offers_schedule_id: '',
    },
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    unitsConfiguration() {
      return this.stateFilters.unitsConfiguration
    },
    hasLoginCard() {
      return this.campaign.targeting_mode === 'ALL' || this.campaign.targeting_mode === 'NON_LOGGED'
    },
    allCards() {
      // Combina todos os cards em uma única array quando não há card de login
      return [...this.firstRowCards, ...this.secondRowCards]
    },
    firstRowCards() {
      const cards = [
        {
          text: 'Pushes entregues',
          value: this.locale(this.metrics?.general_metrics?.general?.messages_received || 0, 0),
          underText: `${this.locale(
            this.getSafe(() => this.metrics?.general_metrics?.general?.messages_received / this.metrics?.general_metrics?.general?.messages_sent) * 100 || 0,
            1
          )}%`,
          description: `dos ${this.locale(this.metrics?.general_metrics?.general?.messages_sent)} pushes enviados`,
          isCurrency: false,
          isPercent: false,
        },
      ]

      // Adicionar card de "Logins Efetuados" apenas quando targeting_mode for 'ALL' ou 'NON_LOGGED'
      if (this.campaign.targeting_mode === 'ALL' || this.campaign.targeting_mode === 'NON_LOGGED') {
        const loginCard = {
          text: 'Logins efetuados',
          value: this.locale(this.metrics?.app_metrics?.login_metrics?.post_login_total || 0, 0),
          underText: `${this.locale(
            this.getSafe(
              () => this.metrics?.app_metrics?.login_metrics?.post_login_total / this.metrics?.app_metrics?.login_metrics?.received_dispatcher_total
            ) * 100 || 0,
            1
          )}%`,
          description: `dos ${this.locale(this.metrics?.app_metrics?.login_metrics?.received_dispatcher_total || 0)} dos aplicativos que receberam`,
          isCurrency: false,
          isPercent: false,
        }
        cards.push(loginCard)
      }

      return cards
    },
    secondRowCards() {
      return [
        {
          text: 'Clientes que compraram',
          value: localeString(this.metrics?.clients_metrics?.received_and_bought?.count || 0),
          underText: `${this.locale(
            this.getSafe(() => this.metrics?.clients_metrics?.received_and_bought?.count / this.metrics?.clients_metrics?.received?.count) * 100 || 0,
            1
          )}%`,
          description: `dos clientes que receberam`,
          helpText: 'Quantidade de clientes que receberam Push e realizaram pelo menos uma compra no período de mensuração',
          isInteger: true,
          percentageTooltip: `Clientes que receberam: ${this.locale(this.metrics?.clients_metrics?.received?.count || 0)}`,
        },
        {
          value: this.locale(this.metrics?.clients_metrics?.received_and_bought?.average_ticket || 0, 2),
          text: 'Ticket médio',
          underText: `${this.locale(this.metrics?.analytic_metrics?.average_ticket_received_variation, 1)}%`,
          description: `a ${this.metrics?.analytic_metrics?.average_ticket_received_variation > 0 ? 'mais' : 'menos'} do que quem não recebeu`,
          helpText:
            'Ticket médio dos clientes que receberam a campanha e realizaram pelo menos uma compra no período de mensuração em comparação com o ticket médio dos consumidores do clube no mesmo período dessa campanha',
          isCurrency: true,
        },
        {
          value: localeString(this.metrics?.clients_metrics?.received_and_bought?.revenue || 0, 0),
          text: 'Faturamento',
          underText: `${this.locale(
            this.getSafe(() => this.metrics?.clients_metrics?.received_and_bought?.revenue / this.metrics?.clients_metrics?.total?.revenue) * 100 || 0,
            1
          )}%`,
          description: `do faturamento total`,
          helpText:
            'Faturamento dos clientes que receberam e compraram em comparação com o faturamento dos consumidores do clube no mesmo período dessa campanha',
          isCurrency: true,
        },
      ]
    },
    units() {
      return this.stateFilters.units || []
    },
  },
  watch: {
    $route(route) {
      if (route.params?.id?.length !== 24 && route.query?.quick_action) {
        // Reset the data
        Object.assign(this.$data, this.$options.data.apply(this))
      }
    },
  },
  async mounted() {
    this.campaignId = this.$route.params.id
    if (this.campaignId?.length !== 24) this.campaignId = ''
    try {
      this.loadingDialog = true
      await this.getCampaign()
      await this.getCampaignMetrics()
    } catch (error) {
      console.log(error)
    } finally {
      this.loadingDialog = false
    }
    if (this.campaign.messages_queues) this.formatData()

    this.$bus.$on('filter', this.handleFilterEvent)
  },
  destroyed() {
    this.$bus.$off('filter')
  },
  methods: {
    handleFilterEvent(payload = {}) {
      if (payload.isInitialLoad) return
      this.getCampaignMetrics()
    },
    async getCampaign() {
      const {
        data: { campaign },
      } = await this.$apollo.query({
        query: QUERY_GET_CAMPAIGN_BY_ID,
        notifyOnNetworkStatusChange: true,
        context: {
          uri: this.$microservicesUrls['campaigns'],
        },
        variables: {
          id: this.campaignId,
        },
        fetchPolicy: 'no-cache',
      })
      let newCampaign = cloneDeep(campaign)

      if (newCampaign && newCampaign.message) {
        if (newCampaign.message.inner_message_title || newCampaign.message.inner_message_text) {
          newCampaign.message.innerMessage = true
        } else {
          newCampaign.message.innerMessage = false
        }
      }

      if (newCampaign && newCampaign.segments) {
        newCampaign.segments = newCampaign.segments.map((segment) => {
          segment.updating = false
          return segment
        })
      }

      if (newCampaign?.offers_schedule && Object.entries(newCampaign.offers_schedule).length) {
        newCampaign = { ...newCampaign, offers_schedule_id: newCampaign.offers_schedule._id }
        this.offerSchedule = newCampaign.offers_schedule
      } else {
        newCampaign = { ...newCampaign, offers_schedule_id: '' }
        this.offerSchedule = {}
      }

      delete newCampaign.offers_schedule
      this.campaign = cloneDeep(newCampaign)
      if (newCampaign.units?.length) this.campaignUnitIds = newCampaign.units.map((unit) => unit.id_erp)
    },
    async getCampaignMetrics() {
      try {
        this.loadingMetrics = true
        const {
          data: { campaign },
        } = await this.$apollo.query({
          query: QUERY_GET_CAMPAIGN_METRICS,
          notifyOnNetworkStatusChange: true,
          variables: {
            id: this.campaign._id,
            units: this.units,
          },
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
          fetchPolicy: 'no-cache',
        })
        if (!campaign) return
        this.sendings = campaign?.message?.sendings || []
        this.metrics = campaign.metrics
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingMetrics = false
      }
    },
  },
}
</script>
